import { useEffect, useMemo, useState } from 'react';
import { differenceInYears } from 'date-fns';
import { useTranslation } from 'next-i18next';

import { FeedbackMode, useFeedback } from '@phoenix/core/frontend/shared/ui/feedback';

import { useUserPreferencesContext } from '@phoenix/team-m/dashboard/frontend/shared/use-user-preferences-context';
import {
  useCreateUserSettingsMutation,
  useGetUserSettingsQuery,
  useUpdateUserSettingsMutation,
} from '@phoenix/team-m/my-tool-shed/frontend/domain/generated/graphql';
import { useBreakpoints } from '@phoenix/design-system/frontend/ui/breakpoints/use-breakpoints';

import { useCustomerSupportLink } from './use-customer-support-link';

type FeedbackSetting = {
  visited: boolean;
  dateShown: string | null;
};

export type ToolShedFeedbackConfig = {
  question: string;
  onPage?: string;
  showAtSecondVisit?: boolean;
  feedbackMode?: FeedbackMode;
  feedbackLabels?: [string, string];
  eventName?: string;
};

export function useToolShedFeedback({
  question,
  onPage = 'overview',
  showAtSecondVisit = false,
  feedbackMode = FeedbackMode.Scale,
  feedbackLabels,
  eventName = 'mystihl_user_feedback',
}: ToolShedFeedbackConfig) {
  const {
    setActive,
    showFeedbackPanel,
    setFeedbackLabels,
    setQuestion,
    setFeedbackMode,
    isFeedbackPanelOpen,
    setTrackingContext,
    setSupportHint,
    setShowFeedbackBanner,
    setShowFeedbackFab,
    setSecondaryButton,
    hideFeedbackPanel,
    setEventName,
  } = useFeedback();
  const { saveUserPreferences, feedbackPanelAlwaysCollapsed, isLoading: isLoadingPreferences } = useUserPreferencesContext();
  const supportLink = useCustomerSupportLink();

  const [isViewReady, setIsViewReady] = useState(onPage === 'add-tool');
  const { t } = useTranslation('shared');
  const { desktop } = useBreakpoints();
  const [name, context] = [
    `tool-shed.feedback.${onPage}`,
    {
      url: process.env.NEXT_PUBLIC_UI_USER_SETTINGS_GRAPHQL_ENDPOINT,
    },
  ];

  const [{ data }] = useGetUserSettingsQuery({
    variables: { name },
    context: useMemo(() => context, []),
  });
  const [_u, update] = useUpdateUserSettingsMutation();
  const [_c, create] = useCreateUserSettingsMutation();
  const [setting, mutateSettingService] = useMemo(
    () => (data?.userSetting?.setting ? [data.userSetting.setting, update] : [{}, create]),
    [data]
  );
  const mutateSetting = (dateShown?: Date) =>
    mutateSettingService({ name, setting: { visited: true, dateShown: dateShown?.toISOString() } }, context);
  const isMoreThanAYearAgo = (date: string): boolean => differenceInYears(new Date(), new Date(date)) > 1;

  useEffect(() => {
    if (data && isViewReady) {
      const { dateShown, visited } = setting as FeedbackSetting;

      if (!visited && showAtSecondVisit) {
        mutateSetting();
      } else if (!isLoadingPreferences && !feedbackPanelAlwaysCollapsed && (!dateShown || isMoreThanAYearAgo(dateShown))) {
        showFeedbackPanel();
      }
    }
  }, [data, isViewReady]);

  useEffect(() => {
    if (isFeedbackPanelOpen) {
      mutateSetting(new Date());
    }
  }, [isFeedbackPanelOpen, data, isLoadingPreferences]);

  const onClickSecondaryButton = () => {
    saveUserPreferences({
      feedbackPanelAlwaysCollapsed: true,
    });
    hideFeedbackPanel();
  };

  useEffect(() => {
    if (isViewReady) {
      setQuestion(question);
      setFeedbackLabels(
        ...(feedbackLabels ||
          ([t('userFeedback_scaleSatisfaction_label_negative'), t('userFeedback_scaleSatisfaction_label_positive')] as [string, string]))
      );
      setSupportHint(t('userFeedback_consumer_support_copy'), supportLink);
      setActive(true);
      setFeedbackMode(feedbackMode);
      setTrackingContext(`toolShed_${onPage}`);
      feedbackPanelAlwaysCollapsed
        ? setSecondaryButton(undefined)
        : setSecondaryButton({
            text: t('userFeedback_button_secondary'),
            onClick: onClickSecondaryButton,
          });
    }
    return () => setActive(false);
  }, [isViewReady, question, feedbackLabels, feedbackMode, feedbackPanelAlwaysCollapsed, isFeedbackPanelOpen, supportLink]);

  useEffect(() => {
    setShowFeedbackBanner(desktop);
    setShowFeedbackFab(!desktop);
  }, [desktop]);

  useEffect(() => {
    setEventName?.(eventName);
  }, []);

  return { setIsViewReady };
}
